import axios, { AxiosInstance } from 'axios'
import objectToFormData from '../helpers/objectToFormData'

export enum ApiMethods {
    GET = 'get',
    POST = 'post',
    PUT = 'put',
    DELETE = 'delete',
}

export enum ApiRoutes {
    LOGIN = 'auth/login',
    CHECK_AUTH = 'auth/check',
    LOGOUT = 'auth/logout',
    ARTICLES = 'articles',
    BUTTON_SETTINGS = 'button-settings',
    CART = 'cart',
    CUSTOMIZATIONS = 'customizations',
    FONTS = 'fonts',
    INDIVIDUALIZATIONS = 'individualizations',
    INDIVIDUALIZATION_TOGGLE = 'individualization-toggle',
    MEDIA = 'media',
    MEDIA_CATEGORIES = 'media-categories',
    MEDIA_CATEGORIES_AND_COUNT = 'media-categories-count',
    ORDERS = 'orders',
    PLUGIN = 'plugin',
    SERVER = 'server',
    SURCHARGE_ARTICLES = 'surcharge-articles',
    UPDATE_SUBSCRIPTION = 'update-subscription',
    REFRESH_SUBSCRIPTION = 'refresh-subscription',
    FULFILLER = 'fulfiller',
    GLOBAL_SETTINGS = 'global-settings',
    MEDIA_BASE64 = 'media-base64',
    UPDATE_COUNT = 'update/count',
    UPDATE_MIGRATE = 'update/migrate',
    HTACCESS_CHECK = 'htaccess/check',
    HTACCESS_MODIFY = 'htaccess/modify',
    HTACCESS_RESTORE = 'htaccess/restore',
    NOTIFICATION = 'notification',
    CUSTOMER_GROUPS = 'customer-groups',
    CUSTOMER_GROUPS_USED = 'customer-groups-used',
}

class ApiServiceV2 {
    private instance: AxiosInstance

    constructor() {
        const wrapper = document.getElementById('zr-customizer-wrapper')
        const apiBaseUrl = localStorage.getItem('zr_customizer_api_url')

        const url =
            wrapper && wrapper.dataset.api
                ? wrapper.dataset.api
                : (apiBaseUrl ?? '/')

        this.instance = axios.create({
            baseURL: url,
            // withCredentials: true,
        })
    }

    toUrl(
        route: ApiRoutes,
        id?: string | number,
        params?: Record<string, string>
    ): string {
        let url = id ? (`${route}/${id}` as string) : route
        if (params) {
            const filtered: Record<string, string> = {}
            Object.keys(params).forEach((key) => {
                if (params[key] && params[key] !== '') {
                    filtered[key] = params[key]
                }
            })

            const queryString = new URLSearchParams(filtered).toString()
            if (queryString) {
                url += `?${queryString}`
            }
        }
        return url
    }

    async callApi(
        route: string,
        method = ApiMethods.GET,
        data: object = {},
        files: Blob[] = []
    ) {
        const token = localStorage.getItem('zr_customizer_token')
        if (token) {
            this.instance.defaults.headers.common['Authorization'] =
                `Bearer ${token}`
        }

        return await this.instance.request({
            method: method,
            url: route,
            data: objectToFormData({ ...data, ...files }),
            responseType: 'json',
        })
    }

    async post(
        route: ApiRoutes,
        id?: string | number,
        data: object = {},
        files: Blob[] = []
    ) {
        return await this.callApi(
            this.toUrl(route, id),
            ApiMethods.POST,
            data,
            files
        )
    }

    async get(
        route: ApiRoutes,
        id?: string | number,
        params?: Record<string, string>
    ) {
        return await this.callApi(this.toUrl(route, id, params), ApiMethods.GET)
    }

    async delete(route: ApiRoutes, id?: string | number) {
        return await this.callApi(this.toUrl(route, id), ApiMethods.DELETE)
    }
}

export const api = new ApiServiceV2()
